import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiTokenRequest } from './authConfig';
import Dashboard from './components/Dashboard';
import HistoricalDashboard from './components/HistoricalDashboard';
import LogsInsights from './components/LogsInsights';
import SourceInfo from './components/SourceInfo';
import './App.css';
import { NavLink, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Dropdown from "./components/Dropdown";
import {User, LogOut} from 'lucide-react';

function App() {
  const { instance, accounts, inProgress } = useMsal();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const navRef = useRef(null);

  useEffect(() => { const handleClickOutside = (event) => { if ( navRef.current && !navRef.current.contains(event.target) && !event.target.closest(".nav-toggle") ) { setIsOpen(false); } }; 
document.addEventListener("mousedown", handleClickOutside); return () => document.removeEventListener("mousedown", handleClickOutside); }, []);


  const acquireAccessToken = useCallback(async () => {
    try {
      const response = await instance.acquireTokenSilent({
        ...apiTokenRequest,
        account: accounts[0],
      });
      setAccessToken(response.accessToken);
      setError(null);
      setLoading(false);
    } catch (err) {
      if (err.errorCode === 'interaction_required' || err.errorCode === 'consent_required') {
        try {
          const response = await instance.acquireTokenPopup({
            ...apiTokenRequest,
            account: accounts[0],
          });
          setAccessToken(response.accessToken);
          setError(null);
          setLoading(false);
          return;
        } catch (popupErr) {
          console.error('Popup token acquisition failed:', popupErr);
        }
      }
      setError(`Failed to acquire API token. Error code: ${err.errorCode}`);
      setLoading(false);
    }
  }, [instance, accounts]);

  const handleLogin = useCallback(async () => {
    try {
      await instance.loginRedirect({ scopes: ["openid", "profile", "email"] });
    } catch (err) {
      console.error('Login redirect failed:', err);
      setError(err?.message || 'Login failed.');
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => {
    if (inProgress === 'login' || inProgress === 'logout' || inProgress === 'acquireToken') return;
    if (accounts && accounts.length > 0) {
      const activeAccount = instance.getActiveAccount();
      if (!activeAccount) instance.setActiveAccount(accounts[0]);
      setIsAuthenticated(true);
      acquireAccessToken();
    } else {
      setIsAuthenticated(false);
      setLoading(false);
    }
  }, [accounts, inProgress, acquireAccessToken, instance]);

  const handleLogout = async () => {
    await instance.logout();
    setIsAuthenticated(false);
    setAccessToken(null);
  };


  // LOADING STATE
  if (loading) {
    return (
      <div className="app-container loading">
        <div className="spinner"></div>
        <p>Initializing...</p>
      </div>
    );
  }

  // ERROR STATE
  if (error) {
    return (
      <div className="app-container unauthenticated">
        <div className="login-container">
          <h1>H.I.S. NDC Aggregator Dashboard - Travelbrands GIGI</h1>
          <p style={{ color: '#dc2626' }}>Error: {error}</p>
          <button className="btn btn-primary" onClick={handleLogin}>Try Again</button>
        </div>
      </div>
    );
  }

  // NOT AUTHENTICATED
  if (!isAuthenticated) {
    return (
      <div className="app-container unauthenticated">
        <div className="login-container">
          <img src={require('./assets/HISLogo.png')} alt="HIS Logo" className="login-logo" />
          <h3>H.I.S. NDC Aggregator Dashboard <span className='block'>Travelbrands GIGI</span></h3>
          <p>Please sign in with your Microsoft Entra account to access the dashboard.</p>
          <button className="btn btn-primary btn-signin" onClick={handleLogin}>Sign In</button>
        </div>
      </div>
    );
  }

  /*const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/historical-dashboard', label: 'Historical Data' },
  ]*/

  // AUTHENTICATED APP
  return (
    <div className="app-container authenticated">

      {/* HEADER */}
     <header className="app-header ">

      {/* LEFT: Logo + Heading */}
      <div className="header-left d-flex align-items-center gap-2">
        <img
          src={require('./assets/images/his-logo.png')}
          alt="HIS Logo"
          className="header-logo"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/')}
        />
        <h3 className="mb-0">H.I.S. NDC Aggregator Dashboard - Travelbrands GIGI</h3>
      </div>

        {/* CENTER-RIGHT: Dashboard Menu */}
        <nav className={"dashboard-menu navigation gap-3 " + (isOpen ? "open" : "")} ref={navRef}>
          <NavLink  to="/"  className={`nav-link ${window.location.pathname === '/' ? 'active' : ''}`}>Home</NavLink>
          <NavLink  to="/historical-dashboard" className={`nav-link ${window.location.pathname === '/historical-dashboard' ? 'active' : ''}`}>
            Historical Data
          </NavLink>

          <NavLink to="/logs-insights" className={`nav-link ${window.location.pathname === '/logs-insights' ? 'active' : ''}`}>
            Logs and Insights
          </NavLink>

          <NavLink to="/source-info" className={`nav-link ${window.location.pathname === '/source-info' ? 'active' : ''}`}>
            Airline Source Info
          </NavLink>
          {/* <button className={`btn ${window.location.pathname === '/' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => navigate('/')}>
            Home
          </button>
          <button className={`btn ${window.location.pathname === '/historical-dashboard' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => navigate('/historical-dashboard')} >
            Historical Data
          </button> */}
        </nav>

        {/* RIGHT: User + Logout */}

        <Dropdown className="profile header__profile" 
            toggleContent={ 
              <>
                {/* <span className="icon"><CircleUserRound/></span> */}
                <span className="dropdown__avatar">
                  {(accounts.firstName?.[0] || accounts[0]?.name || "U").charAt(0).toUpperCase()} 
                </span>
              </>
              
              }
              items={[
                {label: accounts[0]?.name, iconBefore: <User size={16} />, isTextOnly: true },
                {label: 'Sign Out', iconBefore: <LogOut size={16} />, danger: true, onClick:handleLogout }
                
              ]}
            /> 

            <button className="nav-toggle header__nav--toggle md-hidden" onClick={() => setIsOpen(!isOpen)} >
              <svg className="pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

        {/* <div className="user-section d-flex align-items-center gap-3">
          <div className="user-details">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-user-round-icon lucide-circle-user-round user-icon">
              <path d="M17.925 20.056a6 6 0 0 0-11.851.001"/>
              <circle cx="12" cy="11" r="4"/>
              <circle cx="12" cy="12" r="10"/>
            </svg>
            <span className="username">{accounts[0]?.name}</span>
          </div>
          <button className="btn-logout" onClick={handleLogout}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-log-out-icon lucide-log-out log-out-icon">
              <path d="m16 17 5-5-5-5"/>
              <path d="M21 12H9"/>
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            </svg>
            Sign Out
          </button>
        </div> */}

    </header>

      {/* MAIN CONTENT */}
      <main className="app-main">
        <Routes>
          <Route path="/" element={<Dashboard accessToken={accessToken} />} />          
          <Route path="/historical-dashboard" element={<HistoricalDashboard accessToken={accessToken} />} />
          <Route path="/logs-insights" element={<LogsInsights accessToken={accessToken} />} />
          <Route path="/source-info" element={<SourceInfo accessToken={accessToken} />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
    </div>
  );
}

export default App;